/**
 * Shared data loaders for Bid Analysis template. Used by NC/MAS Direct/MAS Indirect template core hooks.
 */
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { bidAnalysisServices } from 'services/bidAnalysis/bidAnalysisServices';
import type { MarketSalesTrendItem } from '../bidAnalysisTemplate.logic';
import type { UseDataLoadersDeps, UseDataLoadersReturn } from './useDataLoaders.types';

export function useDataLoaders(deps: UseDataLoadersDeps): UseDataLoadersReturn {
  const {
    bidId,
    bidDetails,
    packSizeFormData,
    activePackSizeTab,
    formData,
    chartDateRange,
    tempDateRange,
    marketSalesTrendData,
    selectedCompany,
    setContractsList,
    setHandlingTypesList,
    setSupplierOptions,
    setSupplierName,
    setNotesPanelSection,
    setNdcOptions,
    setChartDateRange,
    setTempDateRange,
    setMarketSalesTrendData,
    setFilteredMarketSalesTrendData,
    setIsLoadingMarketTrends,
    setMarketShareByVolume,
    setIsLoadingMarketShare,
    setVolumeByCot,
    setIsLoadingVolumeByCot,
    setContractPriceRanking,
    setIsLoadingContractPriceRanking,
    notesPanelRef,
    ndcNotFoundShownRef,
    marketSalesTrendAbortControllerRef,
    marketShareAbortControllerRef,
    volumeByCotAbortControllerRef,
    contractPriceRankingAbortControllerRef,
    contractPriceRankingDebounceTimerRef,
    lastContractPriceRankingParamsRef,
    isApplyingFilterRef,
    BidAnalysisStore,
    UserStore,
    supplierName,
    formatDateForChartAPI: formatDateForChartAPIFn,
    applyDateFilter: applyDateFilterFn,
    transformMarketShareData: transformMarketShareDataFn,
  } = deps;

  const loadContractsAndHandlingTypes = useCallback(async () => {
    try {
      const [contracts, handlingTypes] = await Promise.all([
        bidAnalysisServices.getAllIntendedContracts(),
        bidAnalysisServices.getAllGsmsHandlingTypes(),
      ]);
      setContractsList(contracts);
      setHandlingTypesList(handlingTypes);
    } catch (error) {
      console.error('Error loading contracts/handling types:', error);
      setContractsList([]);
      setHandlingTypesList([]);
    }
  }, [setContractsList, setHandlingTypesList]);

  const loadSuppliersForBid = useCallback(
    async (bidIdNum: number) => {
      try {
        const suppliers = await bidAnalysisServices.getSuppliersForBid(bidIdNum);
        const options = suppliers.map(supplier => ({ label: supplier, value: supplier }));
        setSupplierOptions(options);
        if (!supplierName && options.length > 0) {
          setSupplierName(options[0].value);
        }
      } catch (error) {
        console.error('Error loading suppliers:', error);
      }
    },
    [setSupplierOptions, setSupplierName, supplierName]
  );

  const handleNotesClick = useCallback(
    (sectionName: string, event: React.MouseEvent<HTMLButtonElement>) => {
      setNotesPanelSection(sectionName);
      setTimeout(() => {
        if (notesPanelRef.current) {
          notesPanelRef.current.show(event, event.currentTarget);
        }
      }, 0);
    },
    [setNotesPanelSection, notesPanelRef]
  );

  const getCurrentUser = useCallback((): string => {
    const userMetadata = UserStore.userMetadata;
    if (userMetadata?.userInfo?.name) return userMetadata.userInfo.name;
    if (userMetadata?.userInfo?.email) return userMetadata.userInfo.email;
    if (userMetadata) {
      console.warn(
        'UserStore.userMetadata exists but userInfo.name/email not found:',
        userMetadata
      );
    }
    return 'System';
  }, [UserStore]);

  const loadNdcOptions = useCallback(
    async (
      supplier: string,
      concept: string,
      packSize: number,
      forceReload: boolean = false
    ) => {
      if (!supplier || !concept || !packSize) {
        setNdcOptions([]);
        return;
      }
      if (!forceReload) {
        const storedData = BidAnalysisStore.getNdcOptions(supplier, concept, packSize);
        if (storedData && storedData.length > 0) {
          setNdcOptions(storedData);
          return;
        }
      }
      try {
        const ndcData = await bidAnalysisServices.getAllNdcBySupplierConceptPacksize({
          supplier_name: supplier,
          concept_name: concept,
          packsize: packSize,
        });
        const ndcList = Array.isArray(ndcData) ? ndcData : (ndcData as any)?.data || [];
        BidAnalysisStore.setNdcOptions(supplier, concept, packSize, ndcList);
        setNdcOptions(ndcList);
        if (ndcList.length === 0 && !ndcNotFoundShownRef.current) {
          ndcNotFoundShownRef.current = true;
          toast.info('Product Detail Fetching is skipped since NDC is not found', {
            autoClose: 5000,
          });
        }
      } catch (error) {
        console.error('Error loading NDC options:', error);
        setNdcOptions([]);
        if (!ndcNotFoundShownRef.current) {
          ndcNotFoundShownRef.current = true;
          toast.info('Product Detail Fetching is skipped since NDC is not found', {
            autoClose: 5000,
          });
        }
      }
    },
    [setNdcOptions, BidAnalysisStore, ndcNotFoundShownRef]
  );

  const loadContractPriceRankingInternal = useCallback(
    async (
      startDate?: string,
      endDate?: string,
      _forceReload: boolean = false
    ) => {
      const currentData = packSizeFormData[activePackSizeTab ?? 0] || formData;
      if (!bidId || !currentData.conceptName || !currentData.packSize) {
        console.warn('Cannot load contract price ranking: missing bidId, conceptName, or packSize', {
          bidId,
          conceptName: currentData.conceptName,
          packSize: currentData.packSize,
        });
        return;
      }
      const bidIdNum = parseInt(bidId);
      const concept = currentData.conceptName;
      const packSize = parseFloat(currentData.packSize);
      const rawStartDate = startDate || currentData.usagePeriodStartDate || undefined;
      const rawEndDate = endDate || currentData.usagePeriodEndDate || undefined;
      const start_date = rawStartDate ? formatDateForChartAPIFn(rawStartDate) : undefined;
      const end_date = rawEndDate ? formatDateForChartAPIFn(rawEndDate) : undefined;

      const lastParams = lastContractPriceRankingParamsRef.current;
      const paramsChanged =
        !lastParams ||
        lastParams.startDate !== start_date ||
        lastParams.endDate !== end_date ||
        lastParams.concept !== concept ||
        lastParams.packSize !== packSize;

      if (paramsChanged) {
        if (
          contractPriceRankingAbortControllerRef.current &&
          !contractPriceRankingAbortControllerRef.current.signal.aborted
        ) {
          contractPriceRankingAbortControllerRef.current.abort();
        }
      } else {
        return;
      }

      const abortController = new AbortController();
      contractPriceRankingAbortControllerRef.current = abortController;
      lastContractPriceRankingParamsRef.current = {
        startDate: start_date,
        endDate: end_date,
        concept,
        packSize,
      };

      setIsLoadingContractPriceRanking(true);
      try {
        const rankingData = await bidAnalysisServices.getContractPriceRanking(
          bidIdNum,
          {
            concept_name: concept,
            pack_size: packSize,
            start_date: start_date,
            end_date: end_date,
          },
          { signal: abortController.signal }
        );
        const dataArray = Array.isArray(rankingData) ? rankingData : [];
        BidAnalysisStore.setContractPriceRankingData(
          bidIdNum,
          concept,
          packSize,
          dataArray,
          start_date,
          end_date
        );
        setContractPriceRanking(dataArray);
      } catch (error: any) {
        if (
          error?.name === 'AbortError' ||
          error?.name === 'CanceledError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }
        console.error('Error loading contract price ranking:', error);
        setContractPriceRanking([]);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingContractPriceRanking(false);
        }
      }
    },
    [
      packSizeFormData,
      activePackSizeTab,
      formData,
      bidId,
      formatDateForChartAPIFn,
      lastContractPriceRankingParamsRef,
      contractPriceRankingAbortControllerRef,
      BidAnalysisStore,
      setContractPriceRanking,
      setIsLoadingContractPriceRanking,
    ]
  );

  const loadContractPriceRanking = useCallback(
    async (
      startDate?: string,
      endDate?: string,
      forceReload: boolean = false,
      debounceMs: number = 0
    ) => {
      if (contractPriceRankingDebounceTimerRef.current) {
        clearTimeout(contractPriceRankingDebounceTimerRef.current);
        contractPriceRankingDebounceTimerRef.current = null;
      }
      if (debounceMs > 0) {
        return new Promise<void>(resolve => {
          contractPriceRankingDebounceTimerRef.current = setTimeout(async () => {
            contractPriceRankingDebounceTimerRef.current = null;
            await loadContractPriceRankingInternal(startDate, endDate, forceReload);
            resolve();
          }, debounceMs);
        });
      }
      return loadContractPriceRankingInternal(startDate, endDate, forceReload);
    },
    [contractPriceRankingDebounceTimerRef, loadContractPriceRankingInternal]
  );

  const loadMarketSalesTrendData = useCallback(
    async (
      labeller?: string | null,
      _forceReload: boolean = false,
      customStartDate?: Date | string | null,
      customEndDate?: Date | string | null
    ) => {
      if (
        marketSalesTrendAbortControllerRef.current &&
        !marketSalesTrendAbortControllerRef.current.signal.aborted
      ) {
        marketSalesTrendAbortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      marketSalesTrendAbortControllerRef.current = abortController;

      const currentData = packSizeFormData[activePackSizeTab ?? 0] || formData;
      if (!bidId || !currentData.conceptName || !currentData.packSize) {
        console.warn('Cannot load market sales trend: missing bidId, conceptName, or packSize', {
          bidId,
          conceptName: currentData.conceptName,
          packSize: currentData.packSize,
        });
        return;
      }

      const bidIdNum = parseInt(bidId);
      const concept = currentData.conceptName;
      const packSize = parseFloat(currentData.packSize);

      let rawStartDate: Date | string | undefined = undefined;
      let rawEndDate: Date | string | undefined = undefined;

      const hasValidCustomDates =
        customStartDate !== undefined &&
        customStartDate !== null &&
        customStartDate instanceof Date &&
        customEndDate !== undefined &&
        customEndDate !== null &&
        customEndDate instanceof Date;

      if (hasValidCustomDates) {
        rawStartDate = customStartDate;
        rawEndDate = customEndDate;
      } else {
        const storedDateRange = BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize);
        if (storedDateRange !== undefined) {
          if (storedDateRange.startDate && storedDateRange.endDate) {
            rawStartDate = storedDateRange.startDate;
            rawEndDate = storedDateRange.endDate;
          }
        } else if (chartDateRange.startDate && chartDateRange.endDate) {
          rawStartDate = chartDateRange.startDate;
          rawEndDate = chartDateRange.endDate;
        } else {
          let defaultStartDate: string | undefined = undefined;
          let defaultEndDate: string | undefined = undefined;
          if (bidDetails?.form_details?.form_data) {
            const conceptData = bidDetails.form_details.form_data.find(
              (c: any) => c.concept_name === concept
            );
            if (conceptData?.pack_size?.[packSize.toString()]) {
              const packData = conceptData.pack_size[packSize.toString()];
              defaultStartDate = packData.start_date || undefined;
              defaultEndDate = packData.end_date || undefined;
            }
          }
          rawStartDate = currentData.usagePeriodStartDate || defaultStartDate;
          rawEndDate = currentData.usagePeriodEndDate || defaultEndDate;
        }
      }

      const startDate = formatDateForChartAPIFn(rawStartDate);
      const endDate = formatDateForChartAPIFn(rawEndDate);

      setIsLoadingMarketTrends(true);
      try {
        const trendData = await bidAnalysisServices.getMarketSalesTrendByAgency(
          bidIdNum,
          {
            concept,
            pack_size: packSize,
            labeller: labeller || null,
            start_date: startDate,
            end_date: endDate,
          },
          { signal: abortController.signal }
        );
        BidAnalysisStore.setMarketSalesTrendData(bidIdNum, concept, packSize, labeller, trendData);
        setMarketSalesTrendData(trendData);
        if (rawStartDate && rawEndDate) {
          setFilteredMarketSalesTrendData(trendData);
        } else if (chartDateRange.startDate || chartDateRange.endDate) {
          const filtered = applyDateFilterFn(trendData, chartDateRange);
          setFilteredMarketSalesTrendData(filtered);
        } else {
          setFilteredMarketSalesTrendData(trendData);
        }
      } catch (error: any) {
        if (
          error?.name === 'AbortError' ||
          error?.name === 'CanceledError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }
        console.error('Error loading market sales trend:', error);
        setMarketSalesTrendData([]);
        setFilteredMarketSalesTrendData([]);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingMarketTrends(false);
        }
      }
    },
    [
      marketSalesTrendAbortControllerRef,
      packSizeFormData,
      activePackSizeTab,
      formData,
      bidId,
      bidDetails,
      chartDateRange,
      BidAnalysisStore,
      formatDateForChartAPIFn,
      applyDateFilterFn,
      setMarketSalesTrendData,
      setFilteredMarketSalesTrendData,
      setIsLoadingMarketTrends,
    ]
  );

  const loadMarketShareByVolume = useCallback(
    async (
      _forceReload: boolean = false,
      customStartDate?: Date | string | null,
      customEndDate?: Date | string | null
    ) => {
      const currentData = packSizeFormData[activePackSizeTab ?? 0] || formData;
      if (!bidId || !currentData.conceptName || !currentData.packSize) {
        console.warn('Cannot load market share by volume: missing bidId, conceptName, or packSize', {
          bidId,
          conceptName: currentData.conceptName,
          packSize: currentData.packSize,
        });
        return;
      }
      const bidIdNum = parseInt(bidId);
      const concept = currentData.conceptName;
      const packSize = parseFloat(currentData.packSize);

      let rawStartDate: Date | string | undefined = undefined;
      let rawEndDate: Date | string | undefined = undefined;

      const hasValidCustomDates =
        customStartDate !== undefined &&
        customStartDate !== null &&
        customStartDate instanceof Date &&
        customEndDate !== undefined &&
        customEndDate !== null &&
        customEndDate instanceof Date;

      if (hasValidCustomDates) {
        rawStartDate = customStartDate;
        rawEndDate = customEndDate;
      } else {
        const storedDateRange = BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize);
        if (storedDateRange !== undefined) {
          if (storedDateRange.startDate && storedDateRange.endDate) {
            rawStartDate = storedDateRange.startDate;
            rawEndDate = storedDateRange.endDate;
          }
        } else if (chartDateRange.startDate && chartDateRange.endDate) {
          rawStartDate = chartDateRange.startDate;
          rawEndDate = chartDateRange.endDate;
        } else {
          let defaultStartDate: string | undefined = undefined;
          let defaultEndDate: string | undefined = undefined;
          if (bidDetails?.form_details?.form_data) {
            const conceptData = bidDetails.form_details.form_data.find(
              (c: any) => c.concept_name === concept
            );
            if (conceptData?.pack_size?.[packSize.toString()]) {
              const packData = conceptData.pack_size[packSize.toString()];
              defaultStartDate = packData.start_date || undefined;
              defaultEndDate = packData.end_date || undefined;
            }
          }
          rawStartDate = currentData.usagePeriodStartDate || defaultStartDate;
          rawEndDate = currentData.usagePeriodEndDate || defaultEndDate;
        }
      }

      const startDate = formatDateForChartAPIFn(rawStartDate);
      const endDate = formatDateForChartAPIFn(rawEndDate);

      if (
        marketShareAbortControllerRef.current &&
        !marketShareAbortControllerRef.current.signal.aborted
      ) {
        marketShareAbortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      marketShareAbortControllerRef.current = abortController;

      setIsLoadingMarketShare(true);
      try {
        const shareData = await bidAnalysisServices.getMarketShareByVolume(
          bidIdNum,
          {
            concept,
            pack_size: packSize,
            start_date: startDate,
            end_date: endDate,
          },
          { signal: abortController.signal }
        );
        const transformedData = transformMarketShareDataFn(shareData);
        if (!abortController.signal.aborted) {
          BidAnalysisStore.setMarketShareByVolumeData(bidIdNum, concept, packSize, transformedData);
          setMarketShareByVolume(transformedData);
        }
      } catch (error: any) {
        if (
          error?.name === 'AbortError' ||
          error?.name === 'CanceledError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }
        console.error('Error loading market share by volume:', error);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingMarketShare(false);
        }
      }
    },
    [
      packSizeFormData,
      activePackSizeTab,
      formData,
      bidId,
      bidDetails,
      chartDateRange,
      marketShareAbortControllerRef,
      BidAnalysisStore,
      formatDateForChartAPIFn,
      transformMarketShareDataFn,
      setMarketShareByVolume,
      setIsLoadingMarketShare,
    ]
  );

  const loadVolumeByCot = useCallback(
    async (
      labeller?: string | null,
      customStartDate?: Date | string | null,
      customEndDate?: Date | string | null
    ) => {
      const currentData = packSizeFormData[activePackSizeTab ?? 0] || formData;
      if (!currentData.conceptName || !currentData.packSize) {
        return;
      }

      const concept = currentData.conceptName;
      const packSize = parseFloat(currentData.packSize);

      let rawStartDate: Date | string | undefined = undefined;
      let rawEndDate: Date | string | undefined = undefined;

      const hasValidCustomDates =
        customStartDate !== undefined &&
        customStartDate !== null &&
        customStartDate instanceof Date &&
        customEndDate !== undefined &&
        customEndDate !== null &&
        customEndDate instanceof Date;

      if (hasValidCustomDates) {
        rawStartDate = customStartDate;
        rawEndDate = customEndDate;
      } else {
        const bidIdNum = bidId ? parseInt(bidId) : null;
        const storedDateRange = bidIdNum
          ? BidAnalysisStore.getChartDateRange(bidIdNum, concept, packSize)
          : undefined;
        if (storedDateRange !== undefined) {
          if (storedDateRange.startDate && storedDateRange.endDate) {
            rawStartDate = storedDateRange.startDate;
            rawEndDate = storedDateRange.endDate;
          }
        } else if (chartDateRange.startDate && chartDateRange.endDate) {
          rawStartDate = chartDateRange.startDate;
          rawEndDate = chartDateRange.endDate;
        } else {
          rawStartDate = currentData.usagePeriodStartDate;
          rawEndDate = currentData.usagePeriodEndDate;
        }
      }

      const startDate = formatDateForChartAPIFn(rawStartDate);
      const endDate = formatDateForChartAPIFn(rawEndDate);

      if (volumeByCotAbortControllerRef.current && !volumeByCotAbortControllerRef.current.signal.aborted) {
        volumeByCotAbortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      volumeByCotAbortControllerRef.current = abortController;

      setIsLoadingVolumeByCot(true);
      try {
        const data = await bidAnalysisServices.getVolumeByCot(
          {
            concept,
            pack_size: packSize,
            labeller: labeller || null,
            start_date: startDate,
            end_date: endDate,
          },
          { signal: abortController.signal }
        );
        if (!abortController.signal.aborted) {
          setVolumeByCot(Array.isArray(data) ? data : []);
        }
      } catch (error: any) {
        if (
          error?.name === 'AbortError' ||
          error?.name === 'CanceledError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }
        console.error('Error loading volume by COT:', error);
        if (!abortController.signal.aborted) {
          setVolumeByCot([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingVolumeByCot(false);
        }
      }
    },
    [
      packSizeFormData,
      activePackSizeTab,
      formData,
      bidId,
      chartDateRange,
      volumeByCotAbortControllerRef,
      BidAnalysisStore,
      formatDateForChartAPIFn,
      setVolumeByCot,
      setIsLoadingVolumeByCot,
    ]
  );

  const setAndStoreChartDateRange = useCallback(
    (startDate: Date | null, endDate: Date | null) => {
      setChartDateRange({ startDate, endDate });
      const currentData = packSizeFormData[activePackSizeTab ?? 0] || formData;
      if (bidId && currentData.conceptName && currentData.packSize) {
        const bidIdNum = parseInt(bidId);
        const concept = currentData.conceptName;
        const packSize = parseFloat(currentData.packSize);
        BidAnalysisStore.setChartDateRange(bidIdNum, concept, packSize, { startDate, endDate });
      }
    },
    [
      setChartDateRange,
      packSizeFormData,
      activePackSizeTab,
      formData,
      bidId,
      BidAnalysisStore,
    ]
  );

  const applyDateFilterToData = useCallback(
    (data: MarketSalesTrendItem[]) => {
      const filtered = applyDateFilterFn(data, chartDateRange);
      setFilteredMarketSalesTrendData(filtered);
    },
    [applyDateFilterFn, chartDateRange, setFilteredMarketSalesTrendData]
  );

  const handleTempDateRangeChange = useCallback(
    (startDate: Date | null, endDate: Date | null) => {
      setTempDateRange({ startDate, endDate });
    },
    [setTempDateRange]
  );

  const handleApplyDateFilter = useCallback(async () => {
    const { startDate, endDate } = tempDateRange;
    isApplyingFilterRef.current = true;
    setAndStoreChartDateRange(startDate, endDate);

    if (!startDate && !endDate) {
      setFilteredMarketSalesTrendData(marketSalesTrendData);
      setTimeout(() => {
        isApplyingFilterRef.current = false;
      }, 100);
      return;
    }

    applyDateFilterToData(marketSalesTrendData);

    const currentData = packSizeFormData[activePackSizeTab ?? 0] || formData;
    if (bidId && currentData.conceptName && currentData.packSize && startDate && endDate) {
      const startDateStr = formatDateForChartAPIFn(startDate);
      const endDateStr = formatDateForChartAPIFn(endDate);

      if (startDateStr && endDateStr) {
        setIsLoadingMarketTrends(true);
        try {
          await loadMarketSalesTrendData(selectedCompany, true, startDate, endDate);
          await loadMarketShareByVolume(true, startDate, endDate);
          await loadVolumeByCot(selectedCompany, startDate, endDate);
        } catch (error) {
          console.error('Error loading chart data with new dates:', error);
        } finally {
          setIsLoadingMarketTrends(false);
          setTimeout(() => {
            isApplyingFilterRef.current = false;
          }, 200);
        }
      } else {
        isApplyingFilterRef.current = false;
      }
    } else {
      isApplyingFilterRef.current = false;
    }
  }, [
    tempDateRange,
    isApplyingFilterRef,
    setAndStoreChartDateRange,
    setFilteredMarketSalesTrendData,
    marketSalesTrendData,
    applyDateFilterToData,
    packSizeFormData,
    activePackSizeTab,
    formData,
    bidId,
    formatDateForChartAPIFn,
    loadMarketSalesTrendData,
    selectedCompany,
    loadMarketShareByVolume,
    loadVolumeByCot,
    setIsLoadingMarketTrends,
  ]);

  const handleDateRangeChange = useCallback(
    async (startDate: Date | null, endDate: Date | null) => {
      handleTempDateRangeChange(startDate, endDate);
    },
    [handleTempDateRangeChange]
  );

  return {
    loadContractsAndHandlingTypes,
    loadSuppliersForBid,
    getCurrentUser,
    handleNotesClick,
    loadNdcOptions,
    loadMarketSalesTrendData,
    applyDateFilterToData,
    setAndStoreChartDateRange,
    handleTempDateRangeChange,
    handleApplyDateFilter,
    handleDateRangeChange,
    loadMarketShareByVolume,
    loadVolumeByCot,
    loadContractPriceRanking,
  };
}
